import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Fragment, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Box, Row } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { presentCollectionQuantifiers } from '~/components/AiSearchChips/collection-predicate';
import { CollectionChips } from '~/components/AiSearchChips/CollectionChips/CollectionChips';
import { SettingsChipToneProvider } from '~/components/AiSearchChips/settingsChipTone';
import { useLocaleKeys } from '~/i18n';
import { useDraftsServiceGrammar } from '../draftsServiceGrammar';
import { useDraftsAiSearchStore } from '../hooks/useDraftsAiSearchStore';
import { useAgreementNameOptions } from './AgreementNameChip/useAgreementNameOptions';
import { useClientNameOptions } from './ClientNameChip/useClientNameOptions';
import { useContactNameOptions } from './ContactNameChip/useContactNameOptions';
import { useDraftsBundlesGrammar, useDraftsPackagesGrammar } from './draftsCollectionGrammar';
import { useDraftsSettingsGrammar } from './draftsSettingsGrammar';
import { OrGroup } from './OrGroup/OrGroup';
import { buildDraftsPredicateChipTexts, buildPredicateChipUnits } from './PredicateChips/PredicateChips.utils';
export const GetDraftsAiSearchChipsTID = (parentTestId)=>getTestIds(parentTestId);
export const DraftsAiSearchChips = (param)=>{
    let { testId, trailing, isInlineEditMode = false } = param;
    var _ref, _filter_or;
    const { DraftsAiSearchChipsTestIds } = useMemo(()=>GetDraftsAiSearchChipsTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys('proposal.draftsDashboard.aiSearch');
    const packagesGrammar = useDraftsPackagesGrammar();
    const bundlesGrammar = useDraftsBundlesGrammar();
    const { t: commonFilter } = useLocaleKeys('common.filter');
    const { t: agr } = useLocaleKeys('agreement');
    const filter = useDraftsAiSearchStore((s)=>s.filter);
    const { setAgreementName, setClientName, setContactId, setContactName, setStatus, setEffectiveDate, setEffectiveDateMode, setLastUpdatedAt, setLastModifiedBy, setNetTerms, setPaymentSettings, setAgreementSettings, setServices, setPackages, setBundles, setReviewers, setOrArm, toggleConnector, clearGroup } = useDraftsAiSearchStore(useShallow((s)=>({
            setAgreementName: s.setAgreementName,
            setClientName: s.setClientName,
            setContactId: s.setContactId,
            setContactName: s.setContactName,
            setStatus: s.setStatus,
            setEffectiveDate: s.setEffectiveDate,
            setEffectiveDateMode: s.setEffectiveDateMode,
            setLastUpdatedAt: s.setLastUpdatedAt,
            setLastModifiedBy: s.setLastModifiedBy,
            setNetTerms: s.setNetTerms,
            setPaymentSettings: s.setPaymentSettings,
            setAgreementSettings: s.setAgreementSettings,
            setServices: s.setServices,
            setPackages: s.setPackages,
            setBundles: s.setBundles,
            setReviewers: s.setReviewers,
            setOrArm: s.setOrArm,
            toggleConnector: s.toggleConnector,
            clearGroup: s.clearGroup
        })));
    const clientNameOptions = useClientNameOptions();
    const contactNameOptions = useContactNameOptions();
    const agreementNameOptions = useAgreementNameOptions();
    const serviceGrammar = useDraftsServiceGrammar();
    const draftsSettingsGrammar = useDraftsSettingsGrammar();
    if (filter == null) return null;
    const showPackages = filter.packages != null && presentCollectionQuantifiers(filter.packages).length > 0;
    const showBundles = filter.bundles != null && presentCollectionQuantifiers(filter.bundles).length > 0;
    const groupArms = (_ref = (_filter_or = filter.or) !== null && _filter_or !== void 0 ? _filter_or : filter.and) !== null && _ref !== void 0 ? _ref : null;
    const groupTone = filter.or != null ? 'or' : 'and';
    const showGroup = groupArms != null && groupArms.length > 0;
    const predicateTestIds = {
        AgreementName: DraftsAiSearchChipsTestIds.AgreementName(),
        ClientName: DraftsAiSearchChipsTestIds.ClientName(),
        ClientPresence: DraftsAiSearchChipsTestIds.ClientPresence(),
        Reviewers: DraftsAiSearchChipsTestIds.Reviewers(),
        ContactName: DraftsAiSearchChipsTestIds.ContactName(),
        Status: DraftsAiSearchChipsTestIds.Status(),
        EffectiveDate: DraftsAiSearchChipsTestIds.EffectiveDate(),
        EffectiveDateMode: DraftsAiSearchChipsTestIds.EffectiveDateMode(),
        LastUpdated: DraftsAiSearchChipsTestIds.LastUpdated(),
        ModifiedBy: DraftsAiSearchChipsTestIds.ModifiedBy(),
        NetDays: DraftsAiSearchChipsTestIds.NetDays(),
        Settings: DraftsAiSearchChipsTestIds.Settings(),
        Services: DraftsAiSearchChipsTestIds.Services(),
        Packages: DraftsAiSearchChipsTestIds.Packages(),
        Bundles: DraftsAiSearchChipsTestIds.Bundles()
    };
    const callbacks = {
        onAgreementNameChange: setAgreementName,
        onClientNameChange: setClientName,
        onContactIdChange: setContactId,
        onReviewersChange: setReviewers,
        onContactNameChange: setContactName,
        onStatusChange: setStatus,
        onEffectiveDateChange: setEffectiveDate,
        onEffectiveDateModeChange: setEffectiveDateMode,
        onLastUpdatedAtChange: setLastUpdatedAt,
        onLastModifiedByChange: setLastModifiedBy,
        onNetTermsChange: setNetTerms,
        onPaymentSettingsChange: setPaymentSettings,
        onAgreementSettingsChange: setAgreementSettings,
        onServicesChange: setServices
    };
    const predicateTexts = buildDraftsPredicateChipTexts(t, commonFilter, agr);
    const settingsTone = isInlineEditMode ? 'default' : 'warning';
    const settingsGrammar = _object_spread_props(_object_spread({}, draftsSettingsGrammar), {
        tone: settingsTone
    });
    const units = buildPredicateChipUnits({
        predicate: filter,
        testIds: predicateTestIds,
        callbacks,
        clientNameOptions,
        contactNameOptions,
        agreementNameOptions,
        texts: predicateTexts,
        settingsTone,
        settingsGrammar,
        serviceGrammar
    });
    if (showPackages && filter.packages != null) {
        units.push({
            key: 'packages',
            node: /*#__PURE__*/ _jsx(CollectionChips, {
                testId: DraftsAiSearchChipsTestIds.Packages(),
                fieldLabel: t.packagesLabel(),
                nounLabel: t.packagesNoun(),
                collection: filter.packages,
                grammar: packagesGrammar,
                onChange: setPackages
            })
        });
    }
    if (showBundles && filter.bundles != null) {
        units.push({
            key: 'bundles',
            node: /*#__PURE__*/ _jsx(CollectionChips, {
                testId: DraftsAiSearchChipsTestIds.Bundles(),
                fieldLabel: t.bundlesLabel(),
                nounLabel: t.bundlesNoun(),
                collection: filter.bundles,
                grammar: bundlesGrammar,
                onChange: setBundles
            })
        });
    }
    if (showGroup && groupArms != null) {
        units.push({
            key: 'orGroup',
            node: /*#__PURE__*/ _jsx(OrGroup, {
                testId: DraftsAiSearchChipsTestIds.OrGroup(),
                arms: groupArms,
                tone: groupTone,
                onArmChange: setOrArm,
                onToggleConnector: toggleConnector,
                onClearGroup: clearGroup
            })
        });
    }
    return /*#__PURE__*/ _jsx(SettingsChipToneProvider, {
        value: settingsTone,
        children: /*#__PURE__*/ _jsx(Box, {
            testId: DraftsAiSearchChipsTestIds.Root(),
            children: /*#__PURE__*/ _jsxs(Row, {
                alignItems: "center",
                gap: 8,
                wrap: true,
                children: [
                    units.map((unit)=>/*#__PURE__*/ _jsx(Fragment, {
                            children: unit.node
                        }, unit.key)),
                    trailing
                ]
            })
        })
    });
};
